import React, { useEffect, useMemo, useState } from 'react'
import moment from 'moment'
import Select from 'react-select'
import { DatePicker, Tabs } from 'antd'

import Receive from '../../components/reusable/receive'
import { usePet } from '../../modules'

const { TabPane } = Tabs
const { RangePicker } = DatePicker

const money = value => Number(value ?? 0).toLocaleString()

const TrialBalanceTab = () => {
    const [range, setRange] = useState([moment().startOf('month'), moment().endOf('day')])
    const [includeZero, setIncludeZero] = useState(false)
    const [data, setData] = useState()

    const [{ loading, error }, load] = usePet(
        'financeTrialBalance',
        {
            from: range?.[0]?._d ?? range?.[0],
            to: range?.[1]?._d ?? range?.[1],
            includeZero,
        },
        { notify: true, manual: true, callback: ({ data }) => setData(data) },
    )

    useEffect(() => {
        load()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [range?.[0]?.valueOf?.(), range?.[1]?.valueOf?.(), includeZero])

    return (
        <>
            <div className="flex flex-wrap items-center gap-3 mb-4">
                <RangePicker value={ range } onChange={ value => setRange(value ?? [moment().startOf('month'), moment().endOf('day')]) } />
                <label className="text-sm text-gray-700 flex items-center gap-2">
                    <input type="checkbox" checked={ includeZero } onChange={ e => setIncludeZero(e.target.checked) } />
                    Include zero-balance accounts
                </label>
            </div>

            <Receive loading={ loading } error={ error } progress>
                <>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                        <div className="rounded-sm border border-gray-200 bg-white px-4 py-3">
                            <p className="text-xs uppercase text-gray-500 mb-1">Period Debits</p>
                            <p className="text-lg font-semibold mb-0">KSh {money(data?.totals?.periodDebit)}</p>
                        </div>
                        <div className="rounded-sm border border-gray-200 bg-white px-4 py-3">
                            <p className="text-xs uppercase text-gray-500 mb-1">Period Credits</p>
                            <p className="text-lg font-semibold mb-0">KSh {money(data?.totals?.periodCredit)}</p>
                        </div>
                        <div className="rounded-sm border border-gray-200 bg-white px-4 py-3">
                            <p className="text-xs uppercase text-gray-500 mb-1">Closing Debits</p>
                            <p className="text-lg font-semibold mb-0">KSh {money(data?.totals?.closingDebit)}</p>
                        </div>
                        <div className="rounded-sm border border-gray-200 bg-white px-4 py-3">
                            <p className="text-xs uppercase text-gray-500 mb-1">Closing Credits</p>
                            <p className="text-lg font-semibold mb-0">KSh {money(data?.totals?.closingCredit)}</p>
                        </div>
                    </div>

                    <div className="overflow-x-auto">
                        <table className="table-auto w-full">
                            <thead className="text-xs uppercase text-gray-400 bg-gray-50 rounded-sm">
                                <tr>
                                    <th className="p-2 text-left">Account</th>
                                    <th className="p-2 text-left">Path</th>
                                    <th className="p-2 text-right">Opening</th>
                                    <th className="p-2 text-right">Debits</th>
                                    <th className="p-2 text-right">Credits</th>
                                    <th className="p-2 text-right">Closing Debit</th>
                                    <th className="p-2 text-right">Closing Credit</th>
                                </tr>
                            </thead>
                            <tbody className="text-sm font-medium divide-y divide-gray-100">
                                {(data?.rows ?? []).map(row => (
                                    <tr key={ row.accountPath }>
                                        <td className="p-2">{row.name}</td>
                                        <td className="p-2 text-xs text-gray-500">{row.accountPath}</td>
                                        <td className="p-2 text-right">{money(row.openingBalance)}</td>
                                        <td className="p-2 text-right">{money(row.periodDebit)}</td>
                                        <td className="p-2 text-right">{money(row.periodCredit)}</td>
                                        <td className="p-2 text-right">{money(row.closingDebit)}</td>
                                        <td className="p-2 text-right">{money(row.closingCredit)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </>
            </Receive>
        </>
    )
}

const ProfitAndLossTab = () => {
    const [range, setRange] = useState([moment().startOf('month'), moment().endOf('day')])
    const [includeZero, setIncludeZero] = useState(false)
    const [data, setData] = useState()

    const [{ loading, error }, load] = usePet(
        'financeProfitAndLoss',
        {
            from: range?.[0]?._d ?? range?.[0],
            to: range?.[1]?._d ?? range?.[1],
            includeZero,
        },
        { notify: true, manual: true, callback: ({ data }) => setData(data) },
    )

    useEffect(() => {
        load()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [range?.[0]?.valueOf?.(), range?.[1]?.valueOf?.(), includeZero])

    return (
        <>
            <div className="flex flex-wrap items-center gap-3 mb-4">
                <RangePicker value={ range } onChange={ value => setRange(value ?? [moment().startOf('month'), moment().endOf('day')]) } />
                <label className="text-sm text-gray-700 flex items-center gap-2">
                    <input type="checkbox" checked={ includeZero } onChange={ e => setIncludeZero(e.target.checked) } />
                    Include zero-balance accounts
                </label>
            </div>

            <Receive loading={ loading } error={ error } progress>
                <>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
                        <div className="rounded-sm border border-gray-200 bg-white px-4 py-3">
                            <p className="text-xs uppercase text-gray-500 mb-1">Income</p>
                            <p className="text-lg font-semibold mb-0">KSh {money(data?.totals?.totalIncome)}</p>
                        </div>
                        <div className="rounded-sm border border-gray-200 bg-white px-4 py-3">
                            <p className="text-xs uppercase text-gray-500 mb-1">Expenses</p>
                            <p className="text-lg font-semibold mb-0">KSh {money(data?.totals?.totalExpenses)}</p>
                        </div>
                        <div className="rounded-sm border border-gray-200 bg-white px-4 py-3">
                            <p className="text-xs uppercase text-gray-500 mb-1">Net Income</p>
                            <p className="text-lg font-semibold mb-0">KSh {money(data?.totals?.netIncome)}</p>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                        <div className="rounded-sm border border-gray-200 bg-white p-4">
                            <h3 className="font-semibold mb-3">Income</h3>
                            <table className="table-auto w-full">
                                <thead className="text-xs uppercase text-gray-400 bg-gray-50 rounded-sm">
                                    <tr>
                                        <th className="p-2 text-left">Account</th>
                                        <th className="p-2 text-left">Path</th>
                                        <th className="p-2 text-right">Amount</th>
                                    </tr>
                                </thead>
                                <tbody className="text-sm font-medium divide-y divide-gray-100">
                                    {(data?.income ?? []).map(row => (
                                        <tr key={ row.accountPath }>
                                            <td className="p-2">{row.name}</td>
                                            <td className="p-2 text-xs text-gray-500">{row.accountPath}</td>
                                            <td className="p-2 text-right">{money(row.amount)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <div className="rounded-sm border border-gray-200 bg-white p-4">
                            <h3 className="font-semibold mb-3">Expenses</h3>
                            <table className="table-auto w-full">
                                <thead className="text-xs uppercase text-gray-400 bg-gray-50 rounded-sm">
                                    <tr>
                                        <th className="p-2 text-left">Account</th>
                                        <th className="p-2 text-left">Path</th>
                                        <th className="p-2 text-right">Amount</th>
                                    </tr>
                                </thead>
                                <tbody className="text-sm font-medium divide-y divide-gray-100">
                                    {(data?.expenses ?? []).map(row => (
                                        <tr key={ row.accountPath }>
                                            <td className="p-2">{row.name}</td>
                                            <td className="p-2 text-xs text-gray-500">{row.accountPath}</td>
                                            <td className="p-2 text-right">{money(row.amount)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </>
            </Receive>
        </>
    )
}

const BalanceSheetTab = () => {
    const [asOf, setAsOf] = useState(moment())
    const [includeZero, setIncludeZero] = useState(false)
    const [data, setData] = useState()

    const [{ loading, error }, load] = usePet(
        'financeBalanceSheet',
        {
            asOf: asOf?._d ?? asOf,
            includeZero,
        },
        { notify: true, manual: true, callback: ({ data }) => setData(data) },
    )

    useEffect(() => {
        load()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [asOf?.valueOf?.(), includeZero])

    const sections = [
        { label: 'Assets', rows: data?.assets ?? [] },
        { label: 'Liabilities', rows: data?.liabilities ?? [] },
        { label: 'Equity', rows: data?.equity ?? [] },
    ]

    return (
        <>
            <div className="flex flex-wrap items-center gap-3 mb-4">
                <DatePicker value={ asOf } onChange={ value => setAsOf(value ?? moment()) } />
                <label className="text-sm text-gray-700 flex items-center gap-2">
                    <input type="checkbox" checked={ includeZero } onChange={ e => setIncludeZero(e.target.checked) } />
                    Include zero-balance accounts
                </label>
            </div>

            <Receive loading={ loading } error={ error } progress>
                <>
                    <div className="grid grid-cols-2 xl:grid-cols-5 gap-3 mb-4">
                        <div className="rounded-sm border border-gray-200 bg-white px-4 py-3"><p className="text-xs uppercase text-gray-500 mb-1">Assets</p><p className="text-lg font-semibold mb-0">KSh {money(data?.totals?.totalAssets)}</p></div>
                        <div className="rounded-sm border border-gray-200 bg-white px-4 py-3"><p className="text-xs uppercase text-gray-500 mb-1">Liabilities</p><p className="text-lg font-semibold mb-0">KSh {money(data?.totals?.totalLiabilities)}</p></div>
                        <div className="rounded-sm border border-gray-200 bg-white px-4 py-3"><p className="text-xs uppercase text-gray-500 mb-1">Equity</p><p className="text-lg font-semibold mb-0">KSh {money(data?.totals?.totalEquity)}</p></div>
                        <div className="rounded-sm border border-gray-200 bg-white px-4 py-3"><p className="text-xs uppercase text-gray-500 mb-1">Current Earnings</p><p className="text-lg font-semibold mb-0">KSh {money(data?.totals?.currentEarnings)}</p></div>
                        <div className="rounded-sm border border-gray-200 bg-white px-4 py-3"><p className="text-xs uppercase text-gray-500 mb-1">Equation Delta</p><p className="text-lg font-semibold mb-0">KSh {money(data?.totals?.delta)}</p></div>
                    </div>

                    <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
                        {sections.map(section => (
                            <div key={ section.label } className="rounded-sm border border-gray-200 bg-white p-4">
                                <h3 className="font-semibold mb-3">{section.label}</h3>
                                <table className="table-auto w-full">
                                    <thead className="text-xs uppercase text-gray-400 bg-gray-50 rounded-sm">
                                        <tr>
                                            <th className="p-2 text-left">Account</th>
                                            <th className="p-2 text-right">Balance</th>
                                        </tr>
                                    </thead>
                                    <tbody className="text-sm font-medium divide-y divide-gray-100">
                                        {section.rows.map(row => (
                                            <tr key={ row.accountPath }>
                                                <td className="p-2">
                                                    <div>{row.name}</div>
                                                    <p className="mb-0 text-xs text-gray-500">{row.accountPath}</p>
                                                </td>
                                                <td className="p-2 text-right">{money(row.balance)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ))}
                    </div>
                </>
            </Receive>
        </>
    )
}

const CashFlowTab = () => {
    const [range, setRange] = useState([moment().startOf('month'), moment().endOf('day')])
    const [data, setData] = useState()

    const [{ loading, error }, load] = usePet(
        'financeCashFlow',
        {
            from: range?.[0]?._d ?? range?.[0],
            to: range?.[1]?._d ?? range?.[1],
        },
        { notify: true, manual: true, callback: ({ data }) => setData(data) },
    )

    useEffect(() => {
        load()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [range?.[0]?.valueOf?.(), range?.[1]?.valueOf?.()])

    const sections = [
        { label: 'Operating Activities', rows: data?.sections?.operating ?? [], total: data?.totals?.operatingNet },
        { label: 'Investing Activities', rows: data?.sections?.investing ?? [], total: data?.totals?.investingNet },
        { label: 'Financing Activities', rows: data?.sections?.financing ?? [], total: data?.totals?.financingNet },
    ]

    return (
        <>
            <div className="flex flex-wrap items-center gap-3 mb-4">
                <RangePicker value={ range } onChange={ value => setRange(value ?? [moment().startOf('month'), moment().endOf('day')]) } />
            </div>

            <Receive loading={ loading } error={ error } progress>
                <>
                    <div className="grid grid-cols-2 xl:grid-cols-6 gap-3 mb-4">
                        <div className="rounded-sm border border-gray-200 bg-white px-4 py-3"><p className="text-xs uppercase text-gray-500 mb-1">Opening Cash</p><p className="text-lg font-semibold mb-0">KSh {money(data?.totals?.openingCash)}</p></div>
                        <div className="rounded-sm border border-gray-200 bg-white px-4 py-3"><p className="text-xs uppercase text-gray-500 mb-1">Operating</p><p className="text-lg font-semibold mb-0">KSh {money(data?.totals?.operatingNet)}</p></div>
                        <div className="rounded-sm border border-gray-200 bg-white px-4 py-3"><p className="text-xs uppercase text-gray-500 mb-1">Investing</p><p className="text-lg font-semibold mb-0">KSh {money(data?.totals?.investingNet)}</p></div>
                        <div className="rounded-sm border border-gray-200 bg-white px-4 py-3"><p className="text-xs uppercase text-gray-500 mb-1">Financing</p><p className="text-lg font-semibold mb-0">KSh {money(data?.totals?.financingNet)}</p></div>
                        <div className="rounded-sm border border-gray-200 bg-white px-4 py-3"><p className="text-xs uppercase text-gray-500 mb-1">Net Change</p><p className="text-lg font-semibold mb-0">KSh {money(data?.totals?.netChange)}</p></div>
                        <div className="rounded-sm border border-gray-200 bg-white px-4 py-3"><p className="text-xs uppercase text-gray-500 mb-1">Closing Cash</p><p className="text-lg font-semibold mb-0">KSh {money(data?.totals?.closingCash)}</p></div>
                    </div>

                    <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 mb-4">
                        {sections.map(section => (
                            <div key={ section.label } className="rounded-sm border border-gray-200 bg-white p-4">
                                <h3 className="font-semibold mb-3">{section.label}</h3>
                                <table className="table-auto w-full">
                                    <thead className="text-xs uppercase text-gray-400 bg-gray-50 rounded-sm">
                                        <tr>
                                            <th className="p-2 text-left">Activity</th>
                                            <th className="p-2 text-right">Inflows</th>
                                            <th className="p-2 text-right">Outflows</th>
                                            <th className="p-2 text-right">Net</th>
                                        </tr>
                                    </thead>
                                    <tbody className="text-sm font-medium divide-y divide-gray-100">
                                        {section.rows.map(row => (
                                            <tr key={ `${section.label}-${row.label}` }>
                                                <td className="p-2">{row.label}</td>
                                                <td className="p-2 text-right">{money(row.inflows)}</td>
                                                <td className="p-2 text-right">{money(row.outflows)}</td>
                                                <td className="p-2 text-right">{money(row.net)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                                <p className="text-sm text-gray-500 mt-3 mb-0">Net: KSh {money(section.total)}</p>
                            </div>
                        ))}
                    </div>

                    <div className="rounded-sm border border-gray-200 bg-white p-4">
                        <h3 className="font-semibold mb-3">Cash Accounts</h3>
                        <table className="table-auto w-full">
                            <thead className="text-xs uppercase text-gray-400 bg-gray-50 rounded-sm">
                                <tr>
                                    <th className="p-2 text-left">Account</th>
                                    <th className="p-2 text-left">Path</th>
                                    <th className="p-2 text-right">Opening</th>
                                    <th className="p-2 text-right">Change</th>
                                    <th className="p-2 text-right">Closing</th>
                                </tr>
                            </thead>
                            <tbody className="text-sm font-medium divide-y divide-gray-100">
                                {(data?.cashAccounts ?? []).map(row => (
                                    <tr key={ row.accountPath }>
                                        <td className="p-2">{row.name}</td>
                                        <td className="p-2 text-xs text-gray-500">{row.accountPath}</td>
                                        <td className="p-2 text-right">{money(row.openingBalance)}</td>
                                        <td className="p-2 text-right">{money(row.periodChange)}</td>
                                        <td className="p-2 text-right">{money(row.closingBalance)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </>
            </Receive>
        </>
    )
}

const AgingTab = () => {
    const [kind, setKind] = useState({ label: 'Accounts Receivable', value: 'ar' })
    const [asOf, setAsOf] = useState(moment())
    const [string, setString] = useState('')
    const [page, setPage] = useState(1)
    const [data, setData] = useState()

    const kindOptions = useMemo(() => [
        { label: 'Accounts Receivable', value: 'ar' },
        { label: 'Accounts Payable', value: 'ap' },
    ], [])

    const [{ loading, error }, load] = usePet(
        'financeAging',
        {
            kind: kind?.value,
            asOf: asOf?._d ?? asOf,
            string,
            page,
            limit: 20,
        },
        { notify: true, manual: true, callback: ({ data }) => setData(data) },
    )

    useEffect(() => {
        load()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [kind?.value, asOf?.valueOf?.(), string, page])

    return (
        <>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4">
                <Select value={ kind } onChange={ value => { setKind(value); setPage(1) } } options={ kindOptions } />
                <DatePicker value={ asOf } onChange={ value => { setAsOf(value ?? moment()); setPage(1) } } />
                <input
                    value={ string }
                    onChange={ e => { setString(e.target.value); setPage(1) } }
                    className="appearance-none block w-full bg-gray-100 text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight focus:outline-none focus:bg-white"
                    type="text"
                    placeholder="Search subject" />
            </div>

            <Receive loading={ loading } error={ error } progress>
                <>
                    <div className="grid grid-cols-2 xl:grid-cols-6 gap-3 mb-4">
                        {['current', '30', '60', '90', '90+', 'totalOutstanding'].map(bucket => (
                            <div key={ bucket } className="rounded-sm border border-gray-200 bg-white px-4 py-3">
                                <p className="text-xs uppercase text-gray-500 mb-1">{bucket === 'totalOutstanding' ? 'Total' : bucket}</p>
                                <p className="text-lg font-semibold mb-0">KSh {money(data?.totals?.[bucket])}</p>
                            </div>
                        ))}
                    </div>

                    <div className="overflow-x-auto">
                        <table className="table-auto w-full">
                            <thead className="text-xs uppercase text-gray-400 bg-gray-50 rounded-sm">
                                <tr>
                                    <th className="p-2 text-left">Subject</th>
                                    <th className="p-2 text-left">Type</th>
                                    <th className="p-2 text-right">Current</th>
                                    <th className="p-2 text-right">31-60</th>
                                    <th className="p-2 text-right">61-90</th>
                                    <th className="p-2 text-right">91-120</th>
                                    <th className="p-2 text-right">120+</th>
                                    <th className="p-2 text-right">Total</th>
                                </tr>
                            </thead>
                            <tbody className="text-sm font-medium divide-y divide-gray-100">
                                {(data?.rows ?? []).map(row => (
                                    <tr key={ `${row.subjectType}-${row.subject}` }>
                                        <td className="p-2">
                                            <div>{row.displayName}</div>
                                            <p className="mb-0 text-xs text-gray-500">{row.accountPath}</p>
                                        </td>
                                        <td className="p-2">{row.subjectType}</td>
                                        <td className="p-2 text-right">{money(row.buckets?.current)}</td>
                                        <td className="p-2 text-right">{money(row.buckets?.['30'])}</td>
                                        <td className="p-2 text-right">{money(row.buckets?.['60'])}</td>
                                        <td className="p-2 text-right">{money(row.buckets?.['90'])}</td>
                                        <td className="p-2 text-right">{money(row.buckets?.['90+'])}</td>
                                        <td className="p-2 text-right">{money(row.totalOutstanding)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <div className="flex items-center justify-end gap-2 mt-4">
                        <button disabled={ page <= 1 } onClick={ () => setPage(page - 1) } className="btn border border-gray-300 bg-white px-3 py-2">Previous</button>
                        <span className="text-sm text-gray-600">Page {page}</span>
                        <button disabled={ (data?.rows ?? []).length < 20 } onClick={ () => setPage(page + 1) } className="btn border border-gray-300 bg-white px-3 py-2">Next</button>
                    </div>
                </>
            </Receive>
        </>
    )
}

const LedgerDrilldownTab = () => {
    const [accountString, setAccountString] = useState('')
    const [account, setAccount] = useState(null)
    const [range, setRange] = useState([moment().startOf('month'), moment().endOf('day')])
    const [page, setPage] = useState(1)
    const [data, setData] = useState()
    const [accounts, setAccounts] = useState([])

    usePet('accounts', { string: accountString, page: 1, limit: 100, order: 1 }, {
        notify: false,
        callback: ({ data }) => setAccounts((data?.accounts ?? []).map(acc => ({ label: `${acc.name} • ${acc.accountPath ?? acc.type}`, value: acc._id, ...acc }))),
    })

    const [{ loading, error }, load] = usePet(
        'financeLedgerDrilldown',
        {
            account: account?.value,
            from: range?.[0]?._d ?? range?.[0],
            to: range?.[1]?._d ?? range?.[1],
            page,
            limit: 25,
            order: -1,
        },
        { notify: true, manual: true, callback: ({ data }) => setData(data) },
    )

    useEffect(() => {
        if (!account?.value) return
        load()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [account?.value, range?.[0]?.valueOf?.(), range?.[1]?.valueOf?.(), page])

    return (
        <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
                <Select
                    value={ account }
                    onInputChange={ setAccountString }
                    onChange={ value => { setAccount(value); setPage(1) } }
                    options={ accounts }
                    placeholder="Select account" />
                <RangePicker value={ range } onChange={ value => { setRange(value ?? [moment().startOf('month'), moment().endOf('day')]); setPage(1) } } />
            </div>

            <Receive loading={ loading } error={ error } progress condition={ Boolean(account?.value) }>
                <>
                    <div className="rounded-sm border border-gray-200 bg-white px-4 py-3 mb-4">
                        <p className="text-xs uppercase text-gray-500 mb-1">Account</p>
                        <p className="text-lg font-semibold mb-0">{data?.account?.name ?? account?.label}</p>
                        <p className="text-xs text-gray-500 mb-0">{data?.account?.accountPath}</p>
                    </div>

                    <div className="overflow-x-auto">
                        <table className="table-auto w-full">
                            <thead className="text-xs uppercase text-gray-400 bg-gray-50 rounded-sm">
                                <tr>
                                    <th className="p-2 text-left">Date</th>
                                    <th className="p-2 text-left">Posting</th>
                                    <th className="p-2 text-left">Subject</th>
                                    <th className="p-2 text-left">Ref</th>
                                    <th className="p-2 text-right">Debit</th>
                                    <th className="p-2 text-right">Credit</th>
                                    <th className="p-2 text-right">Running</th>
                                </tr>
                            </thead>
                            <tbody className="text-sm font-medium divide-y divide-gray-100">
                                {(data?.rows ?? []).map(row => (
                                    <tr key={ row._id }>
                                        <td className="p-2">{moment(row.date).format('DD MMM YYYY HH:mm')}</td>
                                        <td className="p-2">
                                            <div>{row.postingType ?? '—'}</div>
                                            <p className="mb-0 text-xs text-gray-500">{row.notes}</p>
                                        </td>
                                        <td className="p-2">{row.subjectName ?? '—'}</td>
                                        <td className="p-2">{row.ref ?? '—'}</td>
                                        <td className="p-2 text-right">{money(row.debit)}</td>
                                        <td className="p-2 text-right">{money(row.credit)}</td>
                                        <td className="p-2 text-right">{money(row.runningBalance)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <div className="flex items-center justify-end gap-2 mt-4">
                        <button disabled={ page <= 1 } onClick={ () => setPage(page - 1) } className="btn border border-gray-300 bg-white px-3 py-2">Previous</button>
                        <span className="text-sm text-gray-600">Page {page}</span>
                        <button disabled={ (data?.rows ?? []).length < 25 } onClick={ () => setPage(page + 1) } className="btn border border-gray-300 bg-white px-3 py-2">Next</button>
                    </div>
                </>
            </Receive>
        </>
    )
}

const FinanceStatements = () => (
    <div className="px-4 sm:px-6 lg:px-8 py-8 w-full mx-auto">
        <div className="mb-6">
            <h1 className="text-2xl font-semibold text-gray-800 mb-1">Financial Statements</h1>
            <p className="text-sm text-gray-500 mb-0">Trial balance, P&amp;L, balance sheet, cash flow, aging, and ledger drill-down.</p>
            <p className="text-xs text-gray-400 mb-0">Exports are available from Accounts &gt; Reports through the seeded finance report templates.</p>
        </div>

        <div className="bg-white shadow-lg rounded-sm border border-gray-200 p-4">
            <Tabs defaultActiveKey="trial-balance">
                <TabPane tab="Trial Balance" key="trial-balance">
                    <TrialBalanceTab />
                </TabPane>
                <TabPane tab="P&amp;L" key="pl">
                    <ProfitAndLossTab />
                </TabPane>
                <TabPane tab="Balance Sheet" key="balance-sheet">
                    <BalanceSheetTab />
                </TabPane>
                <TabPane tab="Cash Flow" key="cash-flow">
                    <CashFlowTab />
                </TabPane>
                <TabPane tab="Aging" key="aging">
                    <AgingTab />
                </TabPane>
                <TabPane tab="Ledger Drill-down" key="ledger">
                    <LedgerDrilldownTab />
                </TabPane>
            </Tabs>
        </div>
    </div>
)

export default FinanceStatements
